import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Award, Users, Building2, Heart, Target, ShieldCheck, GraduationCap, MapPin, Briefcase, Calendar, BookOpen, Handshake, Sparkles, Quote, HeartPulse, HardHat, UtensilsCrossed, HandHeart, ShoppingBag, Truck, Factory, Rocket, Phone } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { VideoFacade } from "@/components/VideoFacade";
import { img } from "@/lib/images";
import videoPoster from "@/assets/about-video-poster.jpg";
import { site } from "@/lib/site";
import founderImg from "@/assets/about-founder.jpg";
import officeImg from "@/assets/about-office.jpg";
import clientsImg from "@/assets/about-clients.jpg";
import indRealEstate from "@/assets/industry-realestate.jpg";
import indHealthcare from "@/assets/industry-healthcare.jpg";
import indConstruction from "@/assets/industry-construction.jpg";
import indHospitality from "@/assets/industry-hospitality.jpg";
import indRestaurants from "@/assets/industry-restaurants.jpg";
import indRetail from "@/assets/industry-retail.jpg";
import { canonicalLinks, routeMeta } from "@/lib/seo";
import { SmartImage } from "@/components/SmartImage";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      ...routeMeta("/about"),
      { title: "About Locksley A. Cameron, CPA | CCOB And Company PA" },
      { name: "description", content: "Meet the team behind CCOB And Company PA. Led by Locksley A. Cameron, CPA — a decade serving Winter Garden with 30 years of CPA experience." },
      { property: "og:title", content: "About CCOB And Company PA" },
      { property: "og:description", content: "Trusted CPA firm in Winter Garden led by Locksley A. Cameron." },
    ],
    links: canonicalLinks("/about"),
  }),
  component: AboutPage,
});

function AboutPage() {
  const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
  return (
    <>
      <PageHero eyebrow="About Us" title="A Modern CPA Firm Built on Three Decades of Trust" subtitle="Locksley A. Cameron, CPA founded CCOB And Company PA to give Winter Garden businesses and families honest, personal, and forward-thinking financial guidance." image={img.heroAbout} />

      <section className="py-24 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
        <div className="container-x grid lg:grid-cols-2 gap-14 items-center relative">
          <motion.div {...fadeUp} className="relative group">
            <div className="absolute -inset-4 bg-gradient-to-br from-gold/40 via-primary/20 to-transparent rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-700" />
            <div className="absolute -top-3 -left-3 h-20 w-20 border-t-2 border-l-2 border-gold rounded-tl-2xl" />
            <div className="absolute -bottom-3 -right-3 h-20 w-20 border-b-2 border-r-2 border-gold rounded-br-2xl" />
            <div className="relative">
              <VideoFacade
                videoId="xQm-Wt85nOM"
                title="CCOB And Company PA — Meet Locksley A. Cameron, CPA"
                poster={videoPoster}
              />
            </div>
            <div className="mt-5 flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-muted-foreground">
              <span className="h-px w-8 bg-gold" />
              Watch Our Story
            </div>
          </motion.div>
          <motion.div {...fadeUp}>
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Our Story</div>
            <h2 className="font-display text-4xl text-primary">Personal CPA service. Modern financial thinking.</h2>
            <div className="gold-line mt-5" />
            <p className="mt-6 text-muted-foreground leading-relaxed">
              For ten years, CCOB And Company PA has served Winter Garden and the surrounding 25-mile community with accounting, auditing, and tax expertise. Our founder Locksley A. Cameron brings 30 years of CPA experience — and a quiet conviction that clients deserve clarity, not jargon.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We meet you where you are: in our Winter Garden office, on a video call, or by phone. We listen first, then we build a financial path that respects your goals and your time.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-secondary/40">
        <div className="container-x">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-display text-4xl text-primary">Our Values</h2>
            <div className="gold-line mt-5 mx-auto" />
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              [Heart, "Client-First", "Every recommendation begins with what's right for you."],
              [ShieldCheck, "Integrity", "Transparent communication and ethical CPA standards."],
              [Target, "Strategic", "Forward-thinking guidance that scales with your goals."],
            ].map(([I, t, d]: any) => (
              <motion.div key={t} {...fadeUp} className="bg-card border border-border rounded-2xl p-7 hover:border-gold transition-colors">
                <I className="h-8 w-8 text-gold" />
                <div className="font-display text-xl text-primary mt-4">{t}</div>
                <p className="text-muted-foreground mt-2 text-sm">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="py-24">
        <div className="container-x grid lg:grid-cols-5 gap-12 items-center">
          <motion.div {...fadeUp} className="lg:col-span-2 relative group">
            <div className="absolute -inset-4 bg-gradient-to-br from-gold/40 via-primary/20 to-transparent rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition-opacity duration-700" />
            <div className="absolute -top-3 -left-3 h-20 w-20 border-t-2 border-l-2 border-gold rounded-tl-2xl z-10" />
            <div className="absolute -bottom-3 -right-3 h-20 w-20 border-b-2 border-r-2 border-gold rounded-br-2xl z-10" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-border aspect-[4/5]">
              <SmartImage src={founderImg} alt="Locksley A. Cameron, CPA — Founder of CCOB And Company PA" sizes="(max-width: 1023px) 100vw, 40vw" className="w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent p-6">
                <div className="text-primary-foreground font-display text-xl">Locksley A. Cameron, CPA</div>
                <div className="text-primary-foreground/80 text-sm">Founder · 30+ years experience</div>
              </div>
            </div>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-3">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Meet The Founder</div>
            <h2 className="font-display text-4xl text-primary">Built on three decades of trusted CPA work.</h2>
            <div className="gold-line mt-5" />
            <p className="mt-6 text-muted-foreground leading-relaxed">
              With three decades of accounting, auditing, and tax leadership, Locksley founded CCOB And Company PA to bring boutique-level CPA care to Central Florida — combining rigorous standards with a calm, plain-spoken approach.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                [GraduationCap, "Certified Public Accountant", "Licensed in the State of Florida"],
                [Briefcase, "30+ Years of Practice", "Public accounting, audit, and tax advisory"],
                [MapPin, "Based in Winter Garden, FL", "Serving Central Florida and beyond"],
                [Award, "Boutique Care", "Personal attention, no junior hand-offs"],
              ].map(([I, t, d]: any) => (
                <div key={t} className="flex gap-4">
                  <div className="h-10 w-10 rounded-full bg-gold/15 grid place-items-center shrink-0"><I className="h-5 w-5 text-gold" /></div>
                  <div>
                    <div className="font-medium text-primary">{t}</div>
                    <div className="text-sm text-muted-foreground">{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-2xl bg-card border border-border p-6 md:p-8">
              <Quote className="h-7 w-7 text-gold" />
              <p className="mt-3 font-display text-xl md:text-2xl text-primary leading-snug">
                "Clients deserve a CPA who explains the 'why' — not just the numbers. Clarity is the most valuable thing we offer."
              </p>
              <div className="mt-4 text-sm text-muted-foreground">— Locksley A. Cameron, CPA · Founder</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OFFICE IMAGE BREAK */}
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
        <SmartImage src={officeImg} alt="Inside the CCOB And Company PA office in Winter Garden, FL" sizes="100vw" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/50 to-primary/10" />
        <div className="absolute inset-0 flex items-end">
          <div className="container-x pb-12 md:pb-16">
            <motion.div {...fadeUp} className="max-w-2xl">
              <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Our Office</div>
              <h2 className="font-display text-3xl md:text-5xl text-primary-foreground leading-tight">Where Winter Garden's businesses come to think clearly about money.</h2>
            </motion.div>
          </div>
        </div>
      </section>

      {/* JOURNEY / TIMELINE */}
      <section className="py-28 bg-secondary/40">
        <div className="container-x">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-20">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Our Journey</div>
            <h2 className="font-display font-light text-4xl md:text-5xl text-primary leading-tight">
              A Decade of Local Trust,<br />Three of Expertise
            </h2>
            <div className="mt-8 h-px w-16 bg-gold/60 mx-auto" />
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            {/* Vertical spine */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-border md:-translate-x-1/2" />

            <div className="space-y-20 md:space-y-28">
              {[
                { y: "1994", t: "A Career Begins", d: "Locksley starts his CPA practice, establishing a foundation of integrity and focusing on small businesses and families." },
                { y: "2014", t: "CCOB And Company PA Founded", d: "The Winter Garden firm opens its doors with a client-first promise and a vision for personalized financial guidance." },
                { y: "2019", t: "Expanded Services", d: "Auditing and full-scope tax planning added to support growing businesses through complex regulatory landscapes." },
                { y: "2024", t: "Ten Years Strong", d: "Serving 500+ clients and 200+ businesses across Central Florida — building on three decades of CPA excellence." },
              ].map((m, i) => {
                const reverse = i % 2 === 1;
                const isLast = i === 3;
                return (
                  <motion.div key={m.y} {...fadeUp} className={`relative flex flex-col md:flex-row items-start md:items-center ${reverse ? "md:flex-row-reverse" : ""}`}>
                    {/* Year side */}
                    <div className={`pl-12 md:pl-0 md:w-1/2 ${reverse ? "md:pl-16 md:text-left" : "md:pr-16 md:text-right"} mb-3 md:mb-0`}>
                      <span className="font-display font-light text-5xl md:text-6xl text-gold tracking-tight">{m.y}</span>
                    </div>

                    {/* Node */}
                    <div className={`absolute left-4 md:left-1/2 md:-translate-x-1/2 w-3 h-3 rounded-full ${isLast ? "bg-gold" : "border border-gold bg-secondary"}`} />

                    {/* Content side */}
                    <div className={`pl-12 md:pl-0 md:w-1/2 ${reverse ? "md:pr-16 md:text-right" : "md:pl-16 md:text-left"}`}>
                      <h3 className="font-display text-xl text-primary font-semibold">{m.t}</h3>
                      <p className={`mt-3 text-muted-foreground leading-relaxed font-light max-w-sm ${reverse ? "md:ml-auto" : ""}`}>
                        {m.d}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24">
        <div className="container-x">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Our Approach</div>
            <h2 className="font-display text-4xl text-primary">How We Work With You</h2>
            <div className="gold-line mt-5" />
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              [BookOpen, "Listen First", "We start by understanding your goals, history, and what's keeping you up at night."],
              [Sparkles, "Educate Always", "We explain the 'why' so you can make confident decisions, not just informed ones."],
              [Calendar, "Plan Year-Round", "Tax season isn't a sprint — we plan ahead so April is a formality."],
              [Handshake, "Stay Available", "Questions don't wait for appointments. We're a phone call away when it matters."],
            ].map(([I, t, d]: any) => (
              <motion.div key={t} {...fadeUp} className="relative bg-card border border-border rounded-2xl p-7 hover:border-gold transition-colors">
                <div className="h-12 w-12 rounded-xl bg-gold/15 grid place-items-center mb-5"><I className="h-6 w-6 text-gold" /></div>
                <div className="font-display text-lg text-primary">{t}</div>
                <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-28 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
        <div className="container-x relative">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <div className="text-gold uppercase tracking-[0.25em] text-xs mb-4">Who We Serve</div>
            <h2 className="font-display font-light text-4xl md:text-5xl text-primary leading-tight">
              Industries we know <span className="italic">inside out.</span>
            </h2>
            <div className="mt-6 h-px w-16 bg-gold/60" />
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-xl">
              Three decades of CPA work across Central Florida means we speak your industry's language — from cash-tight restaurants to complex real estate holdings.
            </p>
          </motion.div>

          {/* Featured industry bento — no gaps */}
          <div className="grid grid-cols-2 sm:grid-cols-4 auto-rows-[180px] sm:auto-rows-[200px] lg:auto-rows-[240px] gap-4 md:gap-5">
            {[
              { label: "Real Estate", desc: "Investors, agencies, REITs", img: indRealEstate, span: "col-span-2 row-span-2" },
              { label: "Healthcare", desc: "Practices & medical groups", img: indHealthcare, span: "col-span-2 row-span-1" },
              { label: "Hospitality", desc: "Hotels & resorts", img: indHospitality, span: "col-span-1 row-span-1" },
              { label: "Construction", desc: "Contractors & developers", img: indConstruction, span: "col-span-1 row-span-1" },
              { label: "Restaurants", desc: "From cafés to fine dining", img: indRestaurants, span: "col-span-2 sm:col-span-2 row-span-1" },
              { label: "Retail", desc: "Boutiques & shops", img: indRetail, span: "col-span-2 sm:col-span-2 row-span-1" },
            ].map(({ label, desc, img: src, span }) => (
              <motion.div key={label} {...fadeUp} className={`group relative overflow-hidden rounded-2xl ring-1 ring-border ${span}`}>
                <SmartImage src={src} alt={`${label} clients served by CCOB And Company PA`} sizes="(max-width: 639px) 50vw, 25vw" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <div className="text-gold uppercase tracking-[0.25em] text-[10px] mb-1.5">{desc}</div>
                  <div className="font-display text-2xl md:text-3xl text-primary-foreground leading-tight">{label}</div>
                </div>
                <div className="absolute top-4 right-4 h-9 w-9 rounded-full bg-primary-foreground/10 backdrop-blur-sm border border-primary-foreground/20 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Sparkles className="h-4 w-4 text-gold" />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Compact secondary industries */}
          <motion.div {...fadeUp} className="mt-10">
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-5 flex items-center gap-3">
              <span className="h-px w-8 bg-gold" /> Plus we serve
            </div>
            <div className="flex flex-wrap gap-2.5">
              {[
                { label: "Nonprofits", Icon: HandHeart },
                { label: "Professional Services", Icon: Briefcase },
                { label: "Logistics", Icon: Truck },
                { label: "Manufacturing", Icon: Factory },
                { label: "Startups", Icon: Rocket },
                { label: "Families", Icon: Users },
              ].map(({ label, Icon }) => (
                <div key={label} className="inline-flex items-center gap-2 bg-card/80 backdrop-blur border border-border rounded-full px-4 py-2 text-sm text-primary hover:border-gold hover:text-gold transition-colors">
                  <Icon className="h-4 w-4 text-gold" strokeWidth={1.75} />
                  {label}
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-x grid sm:grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          {[[Award,"30+","Years CPA Experience"],[Users,"500+","Happy Clients"],[Building2,"200+","Businesses Assisted"],[ShieldCheck,"100%","Licensed & Insured"]].map(([I,v,l]: any) => (
            <motion.div key={l} {...fadeUp} className="bg-card border border-border rounded-2xl p-8">
              <I className="h-7 w-7 text-gold mx-auto" />
              <div className="font-display text-4xl text-primary mt-4">{v}</div>
              <div className="text-sm text-muted-foreground">{l}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-3xl">
            <SmartImage src={clientsImg} alt="Clients meeting with their CPA" sizes="100vw" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/80 to-primary/40" />
            <div className="relative p-10 md:p-16 text-primary-foreground max-w-2xl">
              <div className="text-gold uppercase tracking-[0.25em] text-xs mb-3">Let's Talk</div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight">Let's start a conversation.</h2>
              <p className="mt-4 opacity-90">Schedule a consultation — in-person, virtual, or by phone. We respond within one business day.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href={`tel:${site.phoneRaw}`} className="btn-gold"><Phone className="h-4 w-4" /> Call Us</a>
                <Link to="/contact" hash="consultation" className="btn-outline border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground hover:text-primary">Schedule Consultation</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
